import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import QueryBuilder from './QueryBuilder';
import { expensiveFailureData, expensiveSuccessData, getTotalFailedJobCostDetails, workspaceGetEnv, getonlyWorkspaces, statusOverviewData, fetchSectorList } from 'service/service';
import moment from 'moment';
import PageWrapper from 'components/Common/PageWraper';
import FiltersBar from './FilterBar';

import { QueryRule, FieldOption, useAdbFilters, ENV_OPTIONS_DEFAULT, formatCompactNumber } from 'utils/adb-common-util';

const sectorOptionsList = [
  'latam', 'apac', 'TBD', 'amesa', 'essa', 'flna', 'global', 'tbd', 'pbna', 'pfna', 'snt'
];

interface WorkspaceOptionsParams {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  locationFilter: string;
  selectedDateInString: string;
  navWorkspaceName?: string;
}

const processWorkspaceData = (data: any[], navWorkspaceName?: string) => {
  const workspaceMap = new Map<string, { name: string; id: string }>();

  data.forEach((workspace: any) => {
    if (workspace.workspace_id && workspace.workspace_name) {
      workspaceMap.set(workspace.workspace_id, {
        name: workspace.workspace_name,
        id: workspace.workspace_id
      });
    }
  });

  if (navWorkspaceName && navWorkspaceName !== 'all') {
    const existing = Array.from(workspaceMap.values()).find(
      w => w.name === navWorkspaceName || w.id === navWorkspaceName
    );
    if (!existing) {
      workspaceMap.set(navWorkspaceName, {
        name: navWorkspaceName,
        id: navWorkspaceName
      });
    }
  }

  const workspaceList = Array.from(workspaceMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return [
    { label: 'All', value: 'all' },
    ...workspaceList.map(w => ({
      label: w.name,
      value: w.id,
      workspaceName: w.name,
      workspaceId: w.id
    }))
  ];
};

const processFallbackWorkspace = (navWorkspaceName?: string) => {
  if (navWorkspaceName && navWorkspaceName !== 'all') {
    return [
      { label: 'All', value: 'all' },
      {
        label: navWorkspaceName,
        value: navWorkspaceName,
        workspaceName: navWorkspaceName,
        workspaceId: navWorkspaceName
      }
    ];
  }
  return [{ label: 'All', value: 'all' }];
};

// --- Helper Functions for Data Fetching ---

const buildApiFilters = (queryBuilderFilters: QueryRule[], workspaceOptions: FieldOption[]) => {
  const apiFilters: Array<{ operator: string; field: string; value: string }> = [];
  if (queryBuilderFilters.length > 0) {
    for (const qbFilter of queryBuilderFilters) {
      if (qbFilter.field === "Workspace Name") {
        const match = workspaceOptions.find(
          opt => opt.label === qbFilter.value
        );
        if (match) {
          apiFilters.push({
            operator: 'AND',
            field: 'workspace_name',
            value: match.value
          });
        }
      } else if (qbFilter.field === 'Job') {
        apiFilters.push({
          operator: 'AND',
          field: 'job_name',
          value: qbFilter.value
        });
      } else if (qbFilter.field === 'Status') {
        apiFilters.push({
          operator: 'AND',
          field: 'result_state',
          value: qbFilter.value
        });
      }
    }
  }
  return apiFilters;
};

interface SortingInfo {
  sortBy: string;
  sortDir: 'asc' | 'desc' | '';
}

const determineSortParams = (sortingInfo: SortingInfo) => {
  const keyToApiFieldMap: Record<string, string> = {
    'usage_usd': 'usage_usd',
    'compute_id': 'compute_id',
    'job_name': 'job_name',
    'sku_name': 'sku_name',
    'usage_start_time': 'usage_start_time',
    'cloud': 'cloud',
    'run_name': 'run_name',
    'period_end_time': 'period_end_time',
    'result_state': 'result_state',
    'trigger_type': 'trigger_type',
    'termination_code': 'termination_code',
    'workspace_id': 'workspace_id',
    'workspace_name': 'workspace_name',
    'period_start_time': 'period_start_time',
    'runtime_mins': 'runtime_mins',
    'usage_unit': 'usage_unit',
    'usage_quantity': 'usage_quantity',
    'usage_end_time': 'usage_end_time',
    'job_id': 'job_id',
    'run_id': 'run_id'
  };

  let apiSortField: string | undefined = undefined;
  let apiSortOrder: string | undefined = undefined;

  if (sortingInfo?.sortBy && sortingInfo?.sortDir) {
    const key = String(sortingInfo.sortBy);
    apiSortField = keyToApiFieldMap[key] || key;
    apiSortOrder = sortingInfo.sortDir;
    console.log('📊 Sorting Info:', {
      columnKey: key,
      apiFieldName: apiSortField,
      sortOrder: apiSortOrder,
      mappingExists: !!keyToApiFieldMap[key],
      sortingInfo
    });
  }
  return { apiSortField, apiSortOrder };
};

const getWorkspaceOptions = async ({
  startDate,
  endDate,
  env,
  sector,
  locationFilter,
  selectedDateInString,
  navWorkspaceName
}: WorkspaceOptionsParams): Promise<FieldOption[]> => {
  try {
    const workspaceResp = await getonlyWorkspaces({
      startDate,
      endDate,
      env: env && env !== 'both' ? env : '',
      sector: sector && sector !== 'all' ? sector : '',
      location: locationFilter && locationFilter !== 'ALL' ? locationFilter : '',
      exportPage: 1,
      exportPageSize: 1000,
      filters: [],
      selectedDateInString
    });

    if (workspaceResp?.success && workspaceResp?.data && Array.isArray(workspaceResp.data)) {
      return processWorkspaceData(workspaceResp.data, navWorkspaceName);
    }
  } catch (error) {
    console.error('💥 Error fetching workspace options:', error);
  }

  return processFallbackWorkspace(navWorkspaceName);
};

interface JobOptionsParams {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  locationFilter: string;
  selectedDateInString: string;
  resultState?: string;
  navJobName?: string;
}

const getJobOptions = async ({
  startDate,
  endDate,
  env,
  sector,
  locationFilter,
  selectedDateInString,
  resultState,
  navJobName
}: JobOptionsParams): Promise<FieldOption[]> => {
  try {
    let jobResp;
    // NOTE: The original logic called success data then overwrote it with failure data. 
    // Preserving that behavior here, though it seems redundant.
    if (resultState === 'SUCCEEDED') {
      await expensiveSuccessData({
        startDate,
        endDate,
        env,
        sector,
        location: locationFilter,
        selectedDateInString,
        page: 1,
        limit: 100
      });
    }
    jobResp = await expensiveFailureData({
      startDate,
      endDate,
      env,
      sector,
      location: locationFilter,
      selectedDateInString,
      page: 1,
      limit: 100
    });

    if (jobResp?.success && jobResp?.data?.docs && Array.isArray(jobResp.data.docs)) {
      const jobNameSet = new Set<string>();

      jobResp.data.docs.forEach((job: any) => {
        if (job.job_name && job.job_name.trim() && job.job_name !== '--' && job.job_name !== 'null') {
          jobNameSet.add(job.job_name);
        }
      });

      if (navJobName && navJobName !== 'all' && !jobNameSet.has(navJobName)) {
        jobNameSet.add(navJobName);
      }

      const jobNameList = Array.from(jobNameSet).sort((a, b) => a.localeCompare(b));

      return [
        { label: 'All', value: 'all' },
        ...jobNameList.map(jobName => ({
          label: jobName,
          value: jobName
        }))
      ];
    }
  } catch (error) {
    console.error('💥 Error fetching job options:', error);
  }

  // Fallback
  if (navJobName && navJobName !== 'all') {
    return [
      { label: 'All', value: 'all' },
      { label: navJobName, value: navJobName }
    ];
  }
  return [{ label: 'All', value: 'all' }];
};

function getColumns() {
  return [
    {
      key: 'compute_id',
      name: 'Compute Id',
      formatter({ row }: any) {
        return <div title={row?.compute_id}>{row?.compute_id || '--'}</div>;
      },
      minWidth: 180,
      sortable: true
    },
    {
      key: 'job_name',
      name: 'Job Name',
      formatter({ row }: any) {
        return <div title={row?.job_name}>{row?.job_name || '--'}</div>;
      },
      minWidth: 300,
      sortable: true
    },
    {
      key: 'sku_name',
      name: 'Sku Name',
      formatter({ row }: any) {
        return <div title={row?.sku_name}>{row?.sku_name || '--'}</div>;
      },
      minWidth: 200,
      sortable: true
    },
    {
      key: 'usage_start_time',
      name: 'Usage Start Time',
      formatter({ row }: any) {
        const d = row?.usage_start_time;
        return <div>{d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '--'}</div>;
      },
      minWidth: 180,
      sortable: true
    },
    {
      key: 'cloud',
      name: 'Cloud',
      formatter({ row }: any) {
        return <div>{row?.cloud || '--'}</div>;
      },
      minWidth: 100,
      sortable: true
    },
    {
      key: 'usage_usd',
      name: 'Usage Usd',
      formatter({ row }: any) {
        const v = row?.usage_usd;
        return (
          <div >
            {typeof v === 'number' ? `$${v.toFixed(2)}` : v || '--'}
          </div>
        );
      },
      minWidth: 120,
      sortable: true
    },
    {
      key: 'run_name',
      name: 'Run Name',
      formatter({ row }: any) {
        return <div title={row?.run_name}>{row?.run_name || '--'}</div>;
      },
      minWidth: 350,
      sortable: true
    },
    {
      key: 'period_end_time',
      name: 'Period End Time',
      formatter({ row }: any) {
        const d = row?.period_end_time;
        return <div>{d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '--'}</div>;
      },
      minWidth: 180,
      sortable: true
    },
    {
      key: 'result_state',
      name: 'Result State',
      formatter({ row }: any) {
        const state = row?.result_state || 'UNKNOWN';
        let color = '#ff9800';
        if (state === 'SUCCEEDED') {
          color = '#4caf50';
        } else if (state === 'FAILED') {
          color = '#f44336';
        }
        return <div style={{ color, fontWeight: 500 }}>{state}</div>;
      },
      minWidth: 140,
      sortable: true
    },
    {
      key: 'trigger_type',
      name: 'Trigger Type',
      formatter({ row }: any) {
        return <div>{row?.trigger_type || '--'}</div>;
      },
      minWidth: 120,
      sortable: true
    },
    {
      key: 'termination_code',
      name: 'Termination Code',
      formatter({ row }: any) {
        const code = row?.termination_code || 'N/A';
        let color = '#f44336';
        if (code === 'SUCCESS') {
          color = '#4caf50';
        } else if (code === 'N/A') {
          color = '#999';
        }
        return <div style={{ color }}>{code}</div>;
      },
      minWidth: 160,
      sortable: true
    },
    {
      key: 'workspace_id',
      name: 'Workspace Id',
      formatter({ row }: any) {
        return <div title={row?.workspace_id}>{row?.workspace_id || '--'}</div>;
      },
      minWidth: 180,
      sortable: true
    },
    {
      key: 'period_start_time',
      name: 'Period Start Time',
      formatter({ row }: any) {
        const d = row?.period_start_time;
        return <div>{d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '--'}</div>;
      },
      minWidth: 180,
      sortable: true
    },
    {
      key: 'runtime_mins',
      name: 'Runtime Mins',
      formatter({ row }: any) {
        const v = row?.runtime_mins;
        return (
          <div >
            {typeof v === 'number' ? v : v || '--'}
          </div>
        );
      },
      minWidth: 120,
      sortable: true
    },
    {
      key: 'usage_unit',
      name: 'Usage Unit',
      formatter({ row }: any) {
        return <div>{row?.usage_unit || '--'}</div>;
      },
      minWidth: 100,
      sortable: true
    },
    {
      key: 'usage_quantity',
      name: 'Usage Quantity',
      formatter({ row }: any) {
        const v = row?.usage_quantity;
        return (
          <div >
            {typeof v === 'number' ? formatCompactNumber(v) : v || '--'}
          </div>
        );
      },
      minWidth: 140,
      sortable: true
    },
    {
      key: 'usage_end_time',
      name: 'Usage End Time',
      formatter({ row }: any) {
        const d = row?.usage_end_time;
        return <div>{d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '--'}</div>;
      },
      minWidth: 180,
      sortable: true
    },
    {
      key: 'job_id',
      name: 'Job Id',
      formatter({ row }: any) {
        return <div>{row?.job_id || '--'}</div>;
      },
      minWidth: 150,
      sortable: true
    },
    {
      key: 'run_id',
      name: 'Run Id',
      formatter({ row }: any) {
        return <div>{row?.run_id || '--'}</div>;
      },
      minWidth: 150,
      sortable: true
    },
    {
      key: 'workspace_name',
      name: 'Workspace Name',
      formatter({ row }: any) {
        return <div title={row?.workspace_name}>{row?.workspace_name || '--'}</div>;
      },
      minWidth: 250,
      sortable: true
    }
  ];
}

const FailedJobCostDetails = () => {
  const navigate = useNavigate();

  const {
    startDate, setStartDate, endDate, setEndDate,
    env, setEnv, sector, setSector, sectorName, setSectorName,
    location: locationFilter, setLocation: setLocationFilter,
    selectedDateInString, setSelectedDateInString,
    updateHistoryState, handleDateSelect, envRef, initialEnvAppliedRef, navigationState
  } = useAdbFilters('failed_job_cost_details_filters');

  const {
    dateLabel = 'Failed Job Cost Details',
    type = 'day',
    workspaceName: navWorkspaceName,
    jobName: navJobName,
    computeType: navComputeType,
    jobType: navJobType,
    resultState
  } = navigationState || {};

  const [jobstatusOverview, setJobstatusOverview] = useState<{ _id: string; totalJobCount: number }[]>([]);
  const [envOptionsState, setEnvOptionsState] = useState<{ label: string; value: string }[]>(ENV_OPTIONS_DEFAULT);

  // API state
  const [tableRecords, setTableRecords] = useState<any[]>([]);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationData, setPaginationData] = useState({
    totalElements: 0,
    totalPages: 0
  });

  // Query Builder state
  const [queryBuilderFilters, setQueryBuilderFilters] = useState<QueryRule[]>([]);
  const [queryBuilderKey, setQueryBuilderKey] = useState(0);
  const [workspaceOptions, setWorkspaceOptions] = useState<FieldOption[]>([
    { label: 'All', value: 'all' }
  ]);
  const [jobOptions, setJobOptions] = useState<FieldOption[]>([
    { label: 'All', value: 'all' }
  ]);
  const [sortingInfo, setSortingInfo] = useState<SortingInfo>({ sortBy: '', sortDir: '' });
  const [sectorList, setSectorList] = useState<string[]>([]);

  const fetchSectors = async () => {
    const resp = await fetchSectorList({});
    if (resp?.success) {
      setSectorList(resp?.data || []);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, []);

  // Pre-fill QueryBuilder filters when navigating from workspace charts
  useEffect(() => {
    const initialFilters: QueryRule[] = [];
    // Add workspace name filter if provided
    if (navWorkspaceName && navWorkspaceName !== 'all') {
      initialFilters.push({
        id: '1',
        operator: 'AND',
        field: 'Workspace Name',
        value: navWorkspaceName
      });
    }

    if (navWorkspaceName === undefined) {
      initialFilters.push({
        id: '1',
        operator: 'AND',
        field: 'Workspace Name',
        value: 'all'
      });
    }
    if (navJobName && navJobName !== 'all') {
      initialFilters.push({
        id: '3',
        operator: 'AND',
        field: 'Job',
        value: navJobName
      });
    }
    initialFilters.push({
      id: '2',
      operator: 'AND',
      field: 'Status',
      value: resultState!
    });

    if (initialFilters.length > 0) {
      setQueryBuilderFilters(initialFilters);
      setQueryBuilderKey(prev => prev + 1);
      setPage(1);
    }
  }, [navWorkspaceName, navJobName, navComputeType, resultState]);


  const getEnvFetch = async (loc: string) => {
    try {
      const res = await workspaceGetEnv(loc);
      const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
      setEnvOptionsState(options);

      if (!initialEnvAppliedRef.current && navigationState?.env) {
        setEnv(navigationState.env);
        initialEnvAppliedRef.current = true;
      } else {
        const availableValues = options.map(o => o.value);
        const currentEnv = envRef.current;
        if (!availableValues.includes(currentEnv)) {
          const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
          setEnv(fallback);
        }
      }
    } catch (error) {
      console.error('Failed to fetch environment options:', error);
    }
  };

  const statusOverviewFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
    const startFormatted = startDate.split(' ')[0];
    const endFormatted = endDate.split(' ')[0];

    const resp = await statusOverviewData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
    if (resp?.success) {
      const data = resp.data || [];
      setJobstatusOverview(data);
    }
  };

  useEffect(() => {
    getEnvFetch(locationFilter);
  }, [locationFilter]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const wOptions = await getWorkspaceOptions({
        startDate,
        endDate,
        env,
        sector,
        locationFilter,
        selectedDateInString,
        navWorkspaceName
      });
      setWorkspaceOptions(wOptions);

      const jOptions = await getJobOptions({
        startDate,
        endDate,
        env,
        sector,
        locationFilter,
        selectedDateInString,
        resultState,
        navJobName
      });
      setJobOptions(jOptions);
    };

    fetchFilterOptions();
  }, [startDate, endDate, env, sector, locationFilter, selectedDateInString, navWorkspaceName, navJobName, resultState]);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);

      try {
        const apiFilters = buildApiFilters(queryBuilderFilters, workspaceOptions);
        const { apiSortField, apiSortOrder } = determineSortParams(sortingInfo);

        const resp = await getTotalFailedJobCostDetails({
          startDate,
          endDate,
          env,
          sector,
          location: locationFilter,
          selectedDateInString,
          page,
          limit: pageSize,
          sortField: apiSortField,
          sortOrder: apiSortOrder,
          filters: apiFilters.length > 0 ? apiFilters : undefined
        });

        // Handle API response structure with docs array
        if (resp?.success && resp?.data?.docs && Array.isArray(resp.data.docs)) {
          const records = resp.data.docs;

          // Transform API data (using exact API field names)
          const normalized = records.map((r: any) => {
            return {
              compute_id: r?.compute_id,
              job_name: r?.job_name,
              sku_name: r?.sku_name,
              usage_start_time: r?.usage_start_time,
              cloud: r?.cloud,
              usage_usd: r?.usage_usd,
              run_name: r?.run_name,
              period_end_time: r?.period_end_time,
              result_state: r?.result_state,
              trigger_type: r?.trigger_type,
              termination_code: r?.termination_code,
              workspace_id: r?.workspace_id,
              period_start_time: r?.period_start_time,
              runtime_mins: r?.runtime_mins,
              usage_unit: r?.usage_unit,
              usage_quantity: r?.usage_quantity,
              usage_end_time: r?.usage_end_time,
              job_id: r?.job_id,
              run_id: r?.run_id,
              workspace_name: r?.workspace_name
            };
          });

          setTableRecords(normalized);

          // ✅ Use pagination data from API response
          setPaginationData({
            totalElements: resp.data.pagination?.totalRecords || records.length,
            totalPages: resp.data.pagination?.totalPages || Math.ceil(records.length / pageSize)
          });
        } else {
          console.warn('⚠️ Invalid API response structure:', resp);
          setTableRecords([]);
          setPaginationData({ totalElements: 0, totalPages: 0 });
        }
      } catch (error) {
        console.error('💥 Error fetching data:', error);
        setTableRecords([]);
        setPaginationData({ totalElements: 0, totalPages: 0 });
      } finally {
        setLoader(false);
      }
    };

    fetchData();
    statusOverviewFetch(startDate, endDate, env, sector, locationFilter, selectedDateInString)
  }, [startDate, endDate, env, sector, locationFilter, selectedDateInString, page, pageSize, queryBuilderFilters, workspaceOptions, jobOptions, sortingInfo]);

  const handleExportRequest = async (preferredColumns?: any) => {
    try {
      // Reconstitute apiFilters from queryBuilderFilters state
      // (This logic mirrors what is inside fetchData)
      const exportFilters: Array<{ operator: string; field: string; value: string }> = [];
      if (queryBuilderFilters.length > 0) {
        for (const qbFilter of queryBuilderFilters) {
          if (qbFilter.field === "Workspace Name") {
            const match = workspaceOptions.find(
              opt => opt.label === qbFilter.value
            );
            if (match) {
              exportFilters.push({
                operator: 'AND',
                field: 'workspace_name',
                value: match.value
              });
            }
          } else if (qbFilter.field === 'Job') {
            exportFilters.push({
              operator: 'AND',
              field: 'job_name',
              value: qbFilter.value
            });
          }
          else if (qbFilter.field === 'Environment') {
            exportFilters.push({
              operator: 'AND',
              field: 'result_state',
              value: qbFilter.value
            });
          }
        }
      }

      // Map sort field if needed
      const keyToApiFieldMap: Record<string, string> = {
        'usage_usd': 'usage_usd',
        'compute_id': 'compute_id',
        'job_name': 'job_name',
        'sku_name': 'sku_name',
        'usage_start_time': 'usage_start_time',
        'cloud': 'cloud',
        'run_name': 'run_name',
        'period_end_time': 'period_end_time',
        'result_state': 'result_state',
        'trigger_type': 'trigger_type',
        'termination_code': 'termination_code',
        'workspace_id': 'workspace_id',
        'workspace_name': 'workspace_name',
        'period_start_time': 'period_start_time',
        'runtime_mins': 'runtime_mins',
        'usage_unit': 'usage_unit',
        'usage_quantity': 'usage_quantity',
        'usage_end_time': 'usage_end_time',
        'job_id': 'job_id',
        'run_id': 'run_id'
      };

      let apiSortField: string | undefined = undefined;
      const sortBy = sortingInfo?.sortBy;
      if (sortBy) {
        apiSortField = keyToApiFieldMap[sortBy] || sortBy;
      }

      await getTotalFailedJobCostDetails({
        startDate,
        endDate,
        env,
        sector,
        location: locationFilter,
        selectedDateInString,
        page,
        limit: pageSize,
        sortField: apiSortField,
        sortOrder: sortingInfo.sortDir || undefined,
        filters: exportFilters.length > 0 ? exportFilters : undefined,
        exportType: 'excel'
      });
    } catch (err) {
      console.error('Export error', err);
    }
  };

  const handleSorting = (newSorting: { sortBy: string; sortDir: 'asc' | 'desc' | '' }) => {
    if (newSorting?.sortBy && (newSorting.sortDir === 'asc' || newSorting.sortDir === 'desc')) {
      setSortingInfo({
        sortBy: newSorting.sortBy,
        sortDir: newSorting.sortDir || ''
      });
    } else {
      console.log('❌ Clearing sort (invalid or empty)');
      setSortingInfo({ sortBy: '', sortDir: '' });
    }
  };

  const handleRunQuery = (rules: QueryRule[]) => {

    const validRules = rules.filter(rule => {
      const value = rule.value?.toString().toLowerCase();
      const isValid = value && value !== 'all' && value !== '';
      return isValid;
    });

    setQueryBuilderFilters(validRules);
    setPage(1);
  };

  const handleRunQueryMemo = useCallback(handleRunQuery, []);

  // Table columns
  const defaultColumnNames = [
    'Workspace Id',
    'Workspace Name',
    'Job Id',
    'Job Name',
    'Run Id',
    'Run Name',
    'Compute Id',
    'Sku Name',
    'Cloud',
    'Usage Usd',
    'Period Start Time',
    'Period End Time',
    'Result State',
    'Trigger Type',
    'Termination Code',
    'Runtime Mins',
    'Usage Unit',
    'Usage Quantity',
    'Usage Start Time',
    'Usage End Time',
  ];

  const getActiveFilterName = () => {
    const job = queryBuilderFilters.find(f => f.field === 'Job');
    if (job && job.value && job.value !== 'all') return job.value;

    const env = queryBuilderFilters.find(f => f.field === 'Environment');
    if (env && env.value && env.value !== 'all' && env.value !== '') return env.value;

    const workspaceName = queryBuilderFilters.find(f => f.field === 'Workspace Name');
    if (workspaceName && workspaceName.value && workspaceName.value !== 'all') return workspaceName.value;

    return dateLabel;
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const envrtOptions = useMemo(() => {
    return jobstatusOverview ?
      jobstatusOverview.map(({ _id }) => ({ label: _id, value: _id }))
      : [];
  }, [jobstatusOverview]);

  const memoWorkspaceOptions = useMemo(() => workspaceOptions, [workspaceOptions]);
  const memoJobOptions = useMemo(() => jobOptions, [jobOptions]);
  const memoEnvOptions = useMemo(() => envrtOptions, [envrtOptions]);
  const memoInitialFilters = useMemo(() => queryBuilderFilters, [queryBuilderFilters]);

  const renderTableContent = () => {
    if (loader) {
      return <LoaderComponent pt={20} />;
    }

    if (tableRecords.length === 0) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            backgroundColor: 'white',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            mx: 2
          }}
        >
          <Box sx={{ fontSize: '1.2rem', color: '#666', mb: 1 }}>
            No Data Available
          </Box>
          <Box sx={{ fontSize: '0.9rem', color: '#999' }}>
            No compute spend data found for the selected filters.
          </Box>
          <Box sx={{ fontSize: '0.85rem', color: '#999', mt: 2 }}>
            Date Range: {startDate} to {endDate}
          </Box>
          <Box sx={{ fontSize: '0.85rem', color: '#999' }}>
            Location: {locationFilter} | Environment: {env} | Sector: {sector}
            {navWorkspaceName && ` | Workspace: ${navWorkspaceName}`}
            {navComputeType && ` | Compute Type: ${navComputeType}`}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 2 }}>
        <CustomizedDataGrid
          title={`${getActiveFilterName()} (${paginationData.totalElements.toLocaleString()} records)`}
          tableColumns={getColumns()}
          tableData={tableRecords}
          tableType="adb-cost-spend-table"
          defaultColumnNames={defaultColumnNames}
          page={page}
          pageSize={pageSize}
          paginationData={paginationData}
          listLength={tableRecords.length}
          handlepageSizeChange={handlePageSizeChange}
          handlePageChange={handlePageChange}
          isDateRangeFilter={false}
          isDisabled={false}
          isFetching={loader}
          isSortIcon={true}
          showExport={true}
          exportTableData={handleExportRequest}
          onSorting={handleSorting}
          currSorting={sortingInfo}
        />
      </Box>
    );
  };

  return (
    <PageWrapper>
      <PageHeader
        title={[
          {
            text: 'Job Analysis',
            link: '/databricksADB/jobsADB',
            state: {
              startDate,
              endDate,
              env,
              sector: sectorName,
              location: locationFilter,
              selectedDateInString
            }
          },
          ...(!navJobType ? [{
            text: 'Total Failed Job Cost',
            link: '/databricksADB/failed-jobs-cost',
            state: {
              startDate,
              endDate,
              env,
              sector: sectorName,
              location: locationFilter,
              selectedDateInString
            }
          }] : []),
          {
            text: `${getActiveFilterName()}${((selectedDateInString === 'customDateRange') && (startDate && endDate && startDate !== endDate)) ? ` (${startDate} to ${endDate})` : ''}`
          }
        ]}
        isPepwise2Breadcrumb={true}
      />

      <FiltersBar
        location={locationFilter}
        // pass the actual useState setters (not single-arg wrappers)
        setLocation={setLocationFilter}
        env={env}
        setEnv={setEnv}
        envOptions={envOptionsState}
        setEnvOptions={setEnvOptionsState}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        handleDateSelect={handleDateSelect}
        selectedDateInString={selectedDateInString}
        setSelectedDateInString={setSelectedDateInString}
        sectorName={sectorName}
        // typed wrapper to handle SetStateAction<string> (function or value)
        setSectorName={(v) => {
          const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
          setSectorName(resolved);
          setSector(resolved);
        }}
        updateHistoryState={updateHistoryState}
        sectorOptionsList={sectorList}
        useHeaderFilters
      />

      <Box sx={{ px: 2, mb: 2 }}>
        <QueryBuilder
          key={queryBuilderKey}
          title="Filter Options"
          sectorOptions={memoWorkspaceOptions}
          environmentOptions={memoEnvOptions}
          locationOptions={[]}
          teamFlagOptions={[]}
          jobOptions={memoJobOptions}
          onRunQuery={handleRunQueryMemo}
          showQueryPreference={false}
          initialFilters={memoInitialFilters}
          hideComputeType={false}
          isJob={true}
        />
      </Box>

      {renderTableContent()}
    </PageWrapper>
  );
};

export default FailedJobCostDetails;
